import React, { useEffect, useState } from 'react';
import { Table, Filters } from "botbit-ui-components"
import translate from "../../config/translations/translate";
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from '../../commons/components/application/miscActions';
import { useHistory, useLocation } from 'react-router-dom';
import { getEmployeesForCompany, activateEmployee, deactivateEmployee, deleteEmployee } from '../../api/employeeApi';
import { MDBBadge } from 'mdbreact';
import { setSelectedStoreForReviewLeaderboard } from '../../commons/components/application/applicationActions';
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import JSZip from 'jszip';

import "./ReviewLeaderBoardContainer.scss"

const MySwal = withReactContent(Swal);

const ReviewLeaderBoardContainer = ({ t }) => {
	const availableCompanies = useSelector(state => state.application.availableCompanies) || [];
	const availableStores = useSelector(state => state.application.availableStores) || [];
	const selectedCompany = useSelector(state => state.application.selectedCompany);
	const loggedAdmin = useSelector(state => state.login.admin);
	const selectedStoreForReviewLeaderboard = useSelector(state => state.application.selectedStoreForReviewLeaderboard);
	const [filterValues, setFilterValues] = useState(null);
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const [forceReload, setForceReload] = useState(0);
	const [employees, setEmployees] = useState([]);
	const [isLoading, setIsLoading] = useState(true);

	const [downloadingAll, setDownloadingAll] = useState(false);
		
	useEffect(() => {
		if (location.state && location.state.reload) {
			setForceReload(prev => prev + 1);
			history.replace('/employee', {});
		}
	}, [location, history]);

	useEffect(() => {
		if (selectedCompany && selectedCompany.id) {
			const companyStores = availableStores.filter(store => store.companyId === selectedCompany.id);
			
			const defaultStoreIds = companyStores.length > 0 ? [companyStores[0].id] : [];
			
			const selectedStore = selectedStoreForReviewLeaderboard ?
				(selectedStoreForReviewLeaderboard.id ? [selectedStoreForReviewLeaderboard.id] : [parseInt(selectedStoreForReviewLeaderboard.storeId)]) 
				: defaultStoreIds;
			
			let newFilterValues = { 
				companies: [selectedCompany.id], 
				stores: selectedStore
			};

			setFilterValues(newFilterValues);
		}
	}, []);

	useEffect(() => {
		if (selectedCompany && selectedCompany.id && filterValues) {
			if (!filterValues.companies || filterValues.companies[0] !== selectedCompany.id) {
				const companyStores = availableStores.filter(store => store.companyId === selectedCompany.id);
				
				const defaultStoreIds = companyStores.length > 0 ? [companyStores[0].id] : [];
				const selectedStore = selectedStoreForReviewLeaderboard ?
				(selectedStoreForReviewLeaderboard.id ? [selectedStoreForReviewLeaderboard.id] : [selectedStoreForReviewLeaderboard.storeId]) 
				: defaultStoreIds;
				
				let newFilterValues = { 
					companies: [selectedCompany.id], 
					stores: selectedStore
				};
				
				setFilterValues(newFilterValues);
			}
		}
	}, [selectedCompany]);

	useEffect(() => {
		if (filterValues && filterValues.companies && filterValues.companies.length > 0) {
			setIsLoading(true);
			getEmployees(filterValues).then(data => {
				setEmployees(data.data.data);
			}).catch(error => {
				console.error('Error loading employees:', error);
			}).finally(() => {
				setIsLoading(false);
			});
		}
	}, [filterValues]);

	const getEmployees = async (filters) => {
		dispatch(setLoading(true));
		try {
			let data = await getEmployeesForCompany(
				filters.companies[0], 
				filters.stores
			);
			return data;
		} catch (error) {
			throw error;
		} finally {
			dispatch(setLoading(false));
		}
	};

	const getColumns = () => {
		return [
			{
				dataField: "_rowNumber",
				title: "#",
				size: 5
			},
			{
				dataField: "fullName",
				title: t("name"),
				size: 25
			},
			{
				dataField: "email", 
				title: t("email"),
				size: 25,
				formatter: (cellContent, row) => {
					return <a href={`mailto:${cellContent}`}>{cellContent}</a>
				}
			},
			{
				dataField: "store",
				title: t("store"),
				size: 25,
				formatter: (cellContent, row) => {
					return <span>{row.stores.map(store => store.name).join(", ")}</span>
				}
			},
			{
				dataField: 'active',
				title: t('status'),
				size: 10,
				formatter: (cellContent, row) => {
					return (
						<MDBBadge color={row.active === true ? 'success' : 'danger'}>
							{ row.active ? t("active") : t("inactive") }
						</MDBBadge>
					);
				},
			}
		];
	};

	const getSchemas = () => {
		return {
			companies: {
				id: "companies",
				label: t("company"),
				type: "select",
				icon: "building",
				placeholder: t("company"),
				priority: true,
				visible: availableCompanies.length > 1,
				options: availableCompanies.map(s => ({ value: s.id, text: s.name })),
				defaultValues: filterValues && filterValues.companies,
				search: true
			},
			stores: {
				id: "stores",
				label: t("store"),
				type: "select",
				icon: "store-alt",
				placeholder: t("filters.allStores"),
				priority: true,
				visible: true,
				options: availableStores.map(s => ({ value: s.id, text: s.name || s.storeName, dependencyId: s.companyId })),
				search: true,
				defaultValues: filterValues && filterValues.stores,
				dependsOn: "companies"
			}
		};
	};

	const handleDownloadQR = async (qrLink, employeeName = '', employeeLastName = '', storeName = '') => {
		if (!qrLink) return;
		
		const fullUrl = qrLink.startsWith('http') ? qrLink : `https://${qrLink}`;
		
		const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(fullUrl)}`;
		
		// Helper function to normalize accented characters
		const normalizeText = (text) => {
		  return text
			.normalize('NFD') // Decompose characters with their accents
			.replace(/[\u0300-\u036f]/g, '') // Remove accent marks
			.replace(/\s+/g, '-') // Convert spaces to hyphens
			.replace(/[^A-Za-z0-9-]/g, ''); // Remove other special characters
		};
		
		// Generate a custom filename based on employee info and store name
		const cleanName = normalizeText(employeeName) || 'employee';
		const cleanLastName = normalizeText(employeeLastName) || '';
		const cleanStoreName = normalizeText(storeName) || 'store';
		
		const filename = cleanLastName 
		  ? `${cleanName}-${cleanLastName}-${cleanStoreName}.png`
		  : `${cleanName}-${cleanStoreName}.png`;
		
		try {
		  // Fetch the QR code image
		  const response = await fetch(qrCodeUrl);
		  const blob = await response.blob();
		  
		  // Create a download link
		  const url = window.URL.createObjectURL(blob);
		  const link = document.createElement('a');
		  link.href = url;
		  link.download = filename; // Use the custom filename
		  document.body.appendChild(link);
		  link.click();
		  
		  // Clean up
		  document.body.removeChild(link);
		  window.URL.revokeObjectURL(url);
		} catch (error) {
		  console.error('Error downloading QR code:', error);
		  // Fallback to opening in new tab if download fails
		  window.open(qrCodeUrl, '_blank');
		}
	  }; 

	const handleDownloadAllQRs = async () => {
	if (!employees || employees.length === 0) {
		MySwal.fire({
		type: "warning",
		text: t("noEmployeesToDownload"),
		confirmButtonText: t("close"),
		confirmButtonColor: "#ff7043"
		});
		return;
	}

  setDownloadingAll(true);

  try {
    const zip = new JSZip();

    // Helper function to normalize accented characters
    const normalizeText = (text) => {
      return text
        .normalize('NFD') // Decompose characters with their accents
        .replace(/[\u0300-\u036f]/g, '') // Remove accent marks
        .replace(/\s+/g, '_') // Convert spaces to underscores
        .replace(/[^A-Za-z0-9_-]/g, '_'); // Remove other special characters
    };

    const downloadPromises = employees.map(async (employee, index) => {
      if (!employee.qrLink) return null;

      try {
        const fullUrl = employee.qrLink.startsWith('http') ? employee.qrLink : `https://${employee.qrLink}`;
        const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(fullUrl)}`;

        // Generate filename: name_lastname.png
        const cleanName = normalizeText(employee.name || 'employee');
        const cleanLastName = normalizeText(employee.lastName || '');
        const fileName = cleanLastName 
          ? `${cleanName}_${cleanLastName}.png`
          : `${cleanName}.png`;

        // Fetch the QR code image
        const response = await fetch(qrCodeUrl);
        if (response.ok) {
          const blob = await response.blob();
          zip.file(fileName, blob);
          return { success: true, fileName };
        } else {
          console.error(`Error fetching QR for ${employee.name || 'employee'}: ${response.status}`);
          return { success: false, error: `HTTP ${response.status}` };
        }
      } catch (error) {
        console.error(`Error processing QR for ${employee.name || 'employee'}:`, error);
        return { success: false, error: error.message };
      }
    });

    await Promise.all(downloadPromises);

    // Generate and download the ZIP
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = window.URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `qrs_employees_${selectedCompany.name ? selectedCompany.name : 'company'}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

	// Show success message
     MySwal.fire({
        type: "success",
        text: t("downloadSuccess"),
        confirmButtonText: t("close"),
        confirmButtonColor: "#ff7043"
    });

  } catch (error) {
    console.error('Error downloading all QRs:', error);
    MySwal.fire({
      type: "error",
      text: t("downloadError"),
      confirmButtonText: t("close"),
      confirmButtonColor: "#ff7043"
    });
  } finally {
    setDownloadingAll(false);
  }
};

	const getRowActions = (row, onchange) => {
		let rowActions = [
			{
				key: "employee-actions-download-qr",
				icon: "download",
				label: t("downloadQr"),
				onClick: () => {
					handleDownloadQR(row.qrLink, row.name, row.lastName, row.stores[0].name)
				}
			},
			{
				key: "employee-actions-edit",
				icon: "edit",
				label: t("edit"),
				onClick: () => {
					history.push(`/employee/${row.id}`)
				}
			},
		];

		if (loggedAdmin.operations.includes("ADMINISTRATORS__ACTIVATE")) {
			const action = row.active ? "deactivate" : "activate";
			rowActions.push({
				key: "employee-actions-activation",
				icon:  row.active ? "user-times" : "user-check",
				label: t("rowActions." + action),
				onClick: () => MySwal.fire({
					type: "warning",
					text: t("rowActions.confirmation." + action),
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonText: t("rowActions.confirmation.confirm"),
					confirmButtonColor: "#ff7043",
					cancelButtonText: t("rowActions.confirmation.cancel")
				}).then(res => {
					if (res.value) {
						const invokation = row.active ? deactivateEmployee : activateEmployee;
						invokation(row.id).then((response) => {
							onchange(response.data.data, "update");
							
							MySwal.fire({
								type: "success",
								text: t("rowActions.results.success"),
								confirmButtonText: t("rowActions.results.close"),
								confirmButtonColor: "#ff7043"
							});
						});
					}
				})
			});
		}
		
		if (loggedAdmin.objectAccess === "ALL") {
			rowActions.push({
				key: "employee-actions-delete",
				icon: "trash",
				label: t("delete"),
				onClick: () => MySwal.fire({
					type: "warning",
					text: t("deleteConfirmation"),
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonText: t("confirm"),
					confirmButtonColor: "#ff7043",
					cancelButtonText: t("cancel")
				}).then(res => {
					if (res.value) {
						deleteEmployee(row.id).then((response) => {
							if (response.data.data.deleted) {
								onchange(row, "delete");
								setEmployees(prevEmployees => 
									prevEmployees.filter(emp => emp.id !== row.id)
								);
							} else {
								onchange({...row, active: false}, "update");
								setEmployees(prevEmployees => 
									prevEmployees.map(emp => 
										emp.id === row.id ? {...emp, active: false} : emp
									)
								);
							}
							MySwal.fire({
								type: "warning",
								text: response.data.data.message || t("results.success.delete"),
								confirmButtonText: t("results.close"),
								confirmButtonColor: "#ff7043"
							});
						});
					}
				})
			});
		}
		
		return rowActions;
	};

	return (
		<div className="employees-section">
			<h1 className="main-title">{t("myWorkTeam")}</h1>
			
			<div className="administrators-header">
				<h2 className="section-title">{t("title")}</h2>
				
				<div className="header-buttons">
					{/* Download All QRs Button */}
					{!isLoading && employees.length > 0 && (
						<button
							className="download-all-qrs-button"
							onClick={handleDownloadAllQRs}
							disabled={downloadingAll}
						>
							{downloadingAll ? (
								<>
									<i className="fas fa-spinner fa-spin"></i>
									{t('buttons.downloadingAll')}
								</>
							) : (
								<>
									<i className="fas fa-download"></i>
									{t('buttons.downloadAll')}
								</>
							)}
						</button>
					)}
					
					<button className="btn-new-admin" onClick={() => history.push("/employee/new")}>
						<i className="fas fa-user-plus"></i> {t("newEmployee")}
					</button>
				</div>
			</div>
			
			{filterValues && <Filters
				schemas={getSchemas()}
				values={filterValues}
				onChange={(newValues) => {
					setFilterValues(newValues);
					dispatch(setSelectedStoreForReviewLeaderboard({storeId: newValues.stores[0]}));
				}}
				
				showInfo
			/>}

			
			{filterValues && filterValues.companies && filterValues.companies.length > 0 && (
				<div className="administrators-table-container">
					{isLoading ? (
						<div className="loading-container">
							<p>{t("loading")}</p>
						</div>
					) : employees.length > 0 ? (
						<Table
							filterValues={filterValues}
							getData={(page, order, filters) => Promise.resolve({ data: { data: employees } })}
							pagination={false}
							columns={getColumns()}
							dataPath="data.data"
							elementKey="id"
							className="administrators-table"
							elementActions={(row, onchange) => getRowActions(row, onchange)}
							key={`admins-table-${forceReload}`}
						/>
					) : (
						<div className="empty-state">
							<i className="fas fa-users empty-state-icon"></i>
							<h3>{t("emptyState.title")}</h3>
							<h3>{t("emptyState.description")}</h3>
							<button 
								className="btn-new-admin" 
								onClick={() => history.push("/employee/new")}
							>
								<i className="fas fa-user-plus"></i> {t("newEmployee")}
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

ReviewLeaderBoardContainer.translations = {
	title: {
		en: "Employees",
		es: "Empleados"
	},
	myWorkTeam: {
		en: "My work team",
		es: "Mi equipo de trabajo"
	},
	administrators: {
		en: "Administrators",
		es: "Administradores"
	},
	name: {
		en: "Name",
		es: "Nombre"
	},
	email: {
		en: "Email",
		es: "Correo electrónico"
	},
	store: {
		en: "Store",
		es: "Tienda"
	},
	status: {
		en: "Status",
		es: "Estado"
	},
	active: {
		en: "Active",
		es: "Activo"
	},
	inactive: {
		en: "Inactive",
		es: "Inactivo"
	},
	newAdministrator: {
		en: "New Administrator",
		es: "Nuevo administrador"
	},
	newEmployee: {
		en: "New Employee",
		es: "Nuevo empleado"
	},
	edit: {
		en: "Edit",
		es: "Editar"
	},
	downloadQr: {
		en: "Download QR",
		es: "Descargar QR"
	},
	delete: {
		en: "Delete",
		es: "Borrar"
	},
	deleteConfirmation: {
		en: "Are you sure you want to delete this employee?",
		es: "¿Está seguro de que desea eliminar este empleado?"
	},
	rowActions: {
		edit: {
			en: "Edit",
			es: "Editar"
		},
		delete: {
			en: "Delete",
			es: "Borrar"
		},
		activate: {
			en: "Activate",
			es: "Activar"
		},
		deactivate: {
			en: "Deactivate",
			es: "Desactivar"
		},
		confirmation: {
			delete: {
				en: "Are you sure you want to delete this employee?",
				es: "¿Está seguro que desea eliminar este empleado?"
			},
			activate: {
				en: "Are you sure you want to activate this employee?",
				es: "¿Está seguro que desea activar este empleado?"
			},
			deactivate: {
				en: "Are you sure you want to deactivate this employee?",
				es: "¿Está seguro que desea desactivar este empleado?"
			},
			confirm: {
				en: "Confirm",
				es: "Confirmar"
			},
			cancel: {
				en: "Cancel",
				es: "Cancelar"
			}
		},
		results: {
			success: {
				en: "Operation completed successfully",
				es: "Operación completada exitosamente"
			},
			close: {
				en: "Close",
				es: "Cerrar" 
			}
		}
	},
	actions: {
		en: "Actions",
		es: "Acciones"
	},
	emptyState: {
		title: {
			en: "No employees created yet",
			es: "Aún no hay empleados creados"
		},
		description: {
			en: "To start using this feature, you need to create employees and download their QR codes.",
			es: "Para comenzar a usar esta funcionalidad, necesitas crear empleados y descargar sus códigos QR."
		}
	},
	loading: {
		en: "Loading employees...",
		es: "Cargando empleados..."
	},
	buttons: {
		downloadAll: {
			en: "Download All QRs",
			es: "Descargar todos los QRs"
		},
		downloadingAll: {
			en: "Downloading...",
			es: "Descargando..."
		}
	},
	downloadSuccess: {
		en: "QR Codes downloaded successfully.",
		es: "QR Codes descargados exitosamente."
	},
	downloadError: {
		en: "Error downloading QR Codes. Please try again.",
		es: "Error al descargar los QR Codes. Por favor, intente nuevamente."
	},
	noEmployeesToDownload: {
		en: "No employees to download QR Codes.",
		es: "No hay empleados para descargar QR Codes."
	}
};

export default translate(ReviewLeaderBoardContainer);


